import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import IconButton from "../../../../packages/IconButton";

import {
  calculateCompanySearchInputPosition,
  isElementUnderPinnedColumns,
  isElementVisuallyHidden,
} from "./LicenseColumnFilterDropdown";

import { KEYBOARD_KEYS } from "../../../../constants/global.constants";

import { RemoveIcon,SearchIcon } from "../../../../assets/icons";

import SearchInputBox from "../../../../common/styles/components/inputs/SearchInputBox";

const LicenseCompanyHeaderRenderer = ({
  displayName,
  onSearch,
  currentSearch = "",
  id,
}) => {
  const { t } = useTranslation();
  const iconRef = useRef(null);
  const inputBoxRef = useRef(null);
  const [companySearchState, setCompanySearchState] = useState({
    isSearchInputVisible: false,
    companySearchValue: currentSearch,
    activeFilter: currentSearch,
    companySearchInputPosition: { top: 0, left: 0 },
  });
  const {
    isSearchInputVisible,
    companySearchValue,
    activeFilter,
    companySearchInputPosition,
  } = companySearchState;

  /**
   * useEffect: Sync local state with external currentSearch prop changes
   * This ensures the search input clears when filters are removed externally
   */
  useEffect(() => {
    if (currentSearch === "" && activeFilter !== "") {
      setCompanySearchState((prevState) => ({
        ...prevState,
        companySearchValue: "",
        activeFilter: "",
      }));
    }
  }, [currentSearch]);

  /**
   * useEffect: Handles dynamic positioning and visibility of the company search input box.
   * Updates position on show/hide, window resize, or scroll.
   */
  useEffect(() => {
    if (!isSearchInputVisible) return;
    const updatePosition = () => {
      if (!iconRef.current) return;
      const isHidden =
        isElementVisuallyHidden(iconRef.current) ||
        isElementUnderPinnedColumns(iconRef.current);
      if (isHidden) {
        setCompanySearchState((prevState) => ({
          ...prevState,
          isSearchInputVisible: false,
        }));
        return;
      }
      setCompanySearchState((prevState) => ({
        ...prevState,
        companySearchInputPosition: calculateCompanySearchInputPosition(
          iconRef.current,
        ),
      }));
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isSearchInputVisible]);

  /**
   * useEffect: Handles closing the company search input box when clicking outside.
   * Detects clicks outside the input box and search icon, clears filter if needed.
   */
  useEffect(() => {
    if (!isSearchInputVisible) return;
    const handleOutsideClick = (event) => {
      if (
        inputBoxRef.current &&
        !inputBoxRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        if (activeFilter && !companySearchValue.trim()) {
          onSearch?.("");
          setCompanySearchState((prevState) => ({
            ...prevState,
            companySearchValue: "",
            activeFilter: "",
            isSearchInputVisible: false,
          }));
        } else {
          setCompanySearchState((prevState) => ({
            ...prevState,
            isSearchInputVisible: false,
          }));
        }
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isSearchInputVisible, activeFilter, companySearchValue, onSearch]);

  const openSearchInput = useCallback(() => {
    setCompanySearchState((prevState) => ({
      ...prevState,
      isSearchInputVisible: true,
    }));
  }, []);

  const updateSearchValue = useCallback((e) => {
    setCompanySearchState((prevState) => ({
      ...prevState,
      companySearchValue: e.target.value,
    }));
  }, []);

  const submitSearch = useCallback(() => {
    const sanitized = companySearchValue.trim();
    if (!sanitized && activeFilter) {
      setCompanySearchState((prevState) => ({
        ...prevState,
        companySearchValue: "",
        activeFilter: "",
        isSearchInputVisible: false,
      }));
      onSearch?.("");
      return;
    }
    if (sanitized && sanitized !== activeFilter) {
      setCompanySearchState((prevState) => ({
        ...prevState,
        activeFilter: sanitized,
        isSearchInputVisible: false,
      }));
      onSearch?.(sanitized);
      return;
    }
    setCompanySearchState((prevState) => ({
      ...prevState,
      isSearchInputVisible: false,
    }));
  }, [companySearchValue, activeFilter, onSearch]);

  const clearSearchAndClose = useCallback(() => {
    setCompanySearchState((prevState) => ({
      ...prevState,
      companySearchValue: "",
      activeFilter: "",
      isSearchInputVisible: false,
    }));
    if (activeFilter && onSearch) onSearch("");
  }, [activeFilter, onSearch]);

  const handleInputBlur = useCallback(() => {
    if (activeFilter && !companySearchValue.trim()) {
      setCompanySearchState((prevState) => ({
        ...prevState,
        companySearchValue: "",
        activeFilter: "",
        isSearchInputVisible: false,
      }));
      if (onSearch) onSearch("");
    }
  }, [activeFilter, companySearchValue, onSearch]);

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === KEYBOARD_KEYS.ENTER) {
        submitSearch();
      }
    },
    [submitSearch],
  );

  const clearButtonElement = useMemo(
    () =>
      !!companySearchValue && (
        <IconButton
          id={`${id}-clear-search-btn`}
          onClick={clearSearchAndClose}
          onMouseDown={(e) => e.preventDefault()}
        >
          <RemoveIcon className="clear-search-icon" />
        </IconButton>
      ),
    [companySearchValue, clearSearchAndClose, id],
  );

  const companySearchInputPortalContent = useMemo(
    () => (
      <div
        ref={inputBoxRef}
        className="company-header-search-input-box"
        id={`${id}-search-input`}
        style={{
          top: companySearchInputPosition.top,
          left: companySearchInputPosition.left,
        }}
      >
        <SearchInputBox
          value={companySearchValue}
          onChange={updateSearchValue}
          onSearch={submitSearch}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={t("licenseManagement.searchByCompany")}
          icon={<SearchIcon className="filter-search-icon" />}
          inputText={clearButtonElement}
          unitBgColor="transparent"
          id={`${id}-search-box`}
        />
      </div>
    ),
    [
      companySearchInputPosition,
      companySearchValue,
      updateSearchValue,
      submitSearch,
      handleInputBlur,
      handleInputKeyDown,
      clearButtonElement,
      t,
      id,
    ],
  );

  return (
    <div className="license-company-header-renderer" id={id}>
      <span className="company-header-text">{t(displayName)}</span>
      <span
        ref={iconRef}
        className="company-header-search-icon"
        id={`${id}-search`}
      >
        <IconButton
          id="company-header-search-btn"
          onClick={openSearchInput}
          tooltip={t("ValueStrings.search")}
          placement="top"
        >
          <SearchIcon
            color="var(--du-color-white)"
            width={18}
            height={18}
            fillOpacity={1}
          />
        </IconButton>
      </span>
      {isSearchInputVisible &&
        ReactDOM.createPortal(companySearchInputPortalContent, document.body)}
    </div>
  );
};

export default LicenseCompanyHeaderRenderer;
